import { useEffect, useRef, useState } from 'react';
import {
  motion,
  useMotionTemplate,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  BookOpenText,
  Brush,
  Clock3,
  Download,
  Layers3,
  ShieldCheck,
  Sparkles,
  WandSparkles,
} from 'lucide-react';

import screenDashboard from '../assets/landing/screen-dashboard-landing.jpg';
import screenTextArt from '../assets/landing/screen-text-art-landing.jpg';
import screenTemplates from '../assets/landing/screen-templates-landing.jpg';

const apkDownloadHref =
  import.meta.env.VITE_APK_DOWNLOAD_URL?.trim() || '/AR_Drawing_Master_v2.9_final_updated.apk';
const apkVersionLabel = 'Android APK 2.9';
const apkSizeLabel = '83.2 MB';
const apkUpdatedLabel = 'Updated April 2026';

const stats = [
  { value: '700+', label: 'Templates & references' },
  { value: '4', label: 'Core creation modes' },
  { value: '24/7', label: 'Anytime offline sketch flow' },
];

const featureCards = [
  {
    icon: Brush,
    title: 'AR Trace on Real Paper',
    body: 'Anchor your phone above the page, reduce visual clutter, and trace with a cleaner hand-training loop.',
    image: screenDashboard,
    tone: 'from-[#cfe0ff] to-[#eef4ff]',
  },
  {
    icon: WandSparkles,
    title: 'AI Idea to Sketch',
    body: 'Generate fresh concepts quickly when you run out of references, then immediately continue into tracing.',
    image: screenTextArt,
    tone: 'from-[#f0ddff] to-[#f9f2ff]',
  },
  {
    icon: Layers3,
    title: 'Deep Template Library',
    body: 'Browse multiple categories and switch difficulty without leaving your drawing flow.',
    image: screenTemplates,
    tone: 'from-[#d9f3ff] to-[#edf9ff]',
  },
];

const workflowSteps = [
  {
    title: 'Pick a source',
    body: 'Choose from templates, your own photos, or generated ideas.',
  },
  {
    title: 'Align and trace',
    body: 'Set the guide over your paper and trace in clean passes.',
  },
  {
    title: 'Refine your style',
    body: 'Save progress, revisit references, and improve line confidence.',
  },
];

const detailItems = [
  {
    icon: ShieldCheck,
    title: 'Stable Android-first app',
    body: 'The full drawing experience is in the APK. This website is your product guide and direct installer.',
  },
  {
    icon: Clock3,
    title: 'Built for repeat practice',
    body: 'Not a one-time gimmick: structured tracing loops make daily sketch sessions easier to maintain.',
  },
  {
    icon: BookOpenText,
    title: 'Guided learning path',
    body: 'Lessons, categories, and challenge mechanics help beginners move from copying to confident drawing.',
  },
];

const installSteps = [
  'Tap Download APK and wait for the package to finish downloading.',
  'Open the file from your browser/downloads and allow installation when prompted.',
  'Launch AR Drawing Master and start tracing with templates, text art, and AI modes.',
];

const releaseHighlights = [
  'Smoother app transitions and navigation animations.',
  'Faster loading with route-level code splitting.',
  'Better stability and lower memory pressure while drawing.',
];

const faqItems = [
  {
    q: 'Does this website run the full app in browser?',
    a: 'No. The website is a showcase + download portal. The complete drawing tools run inside the Android APK.',
  },
  {
    q: 'Is the download direct from this website?',
    a: 'Yes. The Download APK button links directly to the latest release package.',
  },
  {
    q: 'Can I use it as a beginner?',
    a: 'Yes. The workflow is designed for beginner and hobby artists with template-guided tracing and repeat practice.',
  },
];

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.22 },
  transition: { duration: 0.6, ease: 'easeOut' as const },
};

export const LandingPage: React.FC = () => {
  const heroRef = useRef<HTMLElement | null>(null);
  const [allowTilt, setAllowTilt] = useState(false);
  const [isMobileLayout, setIsMobileLayout] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });

  useEffect(() => {
    const pointerQuery = window.matchMedia('(pointer: fine)');
    const mobileQuery = window.matchMedia('(max-width: 768px)');
    const update = () => {
      setIsMobileLayout(mobileQuery.matches);
      setAllowTilt(pointerQuery.matches && !mobileQuery.matches && !prefersReducedMotion);
    };
    update();

    pointerQuery.addEventListener('change', update);
    mobileQuery.addEventListener('change', update);
    return () => {
      pointerQuery.removeEventListener('change', update);
      mobileQuery.removeEventListener('change', update);
    };
  }, [prefersReducedMotion]);

  const reduceSceneMotion = prefersReducedMotion || isMobileLayout;

  const sceneY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const glowOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.45]);
  const accentY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  const pointerX = useMotionValue(0);
  const pointerY = useMotionValue(0);

  const rotateY = useSpring(useTransform(pointerX, [-0.5, 0.5], [-11, 11]), {
    stiffness: 180,
    damping: 24,
    mass: 0.7,
  });
  const rotateX = useSpring(useTransform(pointerY, [-0.5, 0.5], [10, -10]), {
    stiffness: 180,
    damping: 24,
    mass: 0.7,
  });

  const glareX = useSpring(useTransform(pointerX, [-0.5, 0.5], [20, 80]), {
    stiffness: 160,
    damping: 22,
  });
  const glareY = useSpring(useTransform(pointerY, [-0.5, 0.5], [20, 80]), {
    stiffness: 160,
    damping: 22,
  });
  const glareBackground = useMotionTemplate`radial-gradient(circle at ${glareX}% ${glareY}%, rgba(255,255,255,0.32), rgba(255,255,255,0) 52%)`;

  const handleScenePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!allowTilt) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width - 0.5;
    const y = (event.clientY - rect.top) / rect.height - 0.5;
    pointerX.set(Math.max(-0.5, Math.min(0.5, x)));
    pointerY.set(Math.max(-0.5, Math.min(0.5, y)));
  };

  const handleScenePointerLeave = () => {
    pointerX.set(0);
    pointerY.set(0);
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#f6f6f3] text-[#0f172a]">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <motion.div
          style={{ y: accentY, opacity: glowOpacity }}
          className="absolute left-[-12rem] top-[-10rem] h-[34rem] w-[34rem] rounded-full bg-[radial-gradient(circle,_rgba(87,134,255,0.18),_rgba(87,134,255,0)_70%)] blur-3xl"
        />
        <motion.div
          style={{ y: sceneY, opacity: glowOpacity }}
          className="absolute right-[-12rem] top-[-6rem] h-[30rem] w-[30rem] rounded-full bg-[radial-gradient(circle,_rgba(255,181,138,0.19),_rgba(255,181,138,0)_74%)] blur-3xl"
        />
      </div>

      <div className="relative">
        <header className="sticky top-0 z-40 border-b border-black/5 bg-white/80 backdrop-blur-2xl">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-5 sm:py-4 md:px-8">
            <a href="#top" className="flex items-center gap-3">
                <img
                  src="/logo-96.png?v=20260409a"
                  alt="AR Drawing Master"
                  className="h-10 w-10 rounded-2xl border border-black/6 object-cover shadow-[0_14px_35px_rgba(15,23,42,0.08)] sm:h-11 sm:w-11"
                  width={96}
                  height={96}
                  loading="eager"
                  decoding="async"
                />
              <div>
                <div className="font-display text-base font-semibold tracking-tight text-[#111111] sm:text-lg">AR Drawing Master</div>
                <div className="hidden text-[11px] uppercase tracking-[0.28em] text-[#71717a] sm:block">Trace. Learn. Create.</div>
              </div>
            </a>

            <nav className="hidden items-center gap-8 text-sm text-[#71717a] md:flex">
              <a href="#experience" className="transition hover:text-[#111111]">Experience</a>
              <a href="#workflow" className="transition hover:text-[#111111]">Workflow</a>
              <a href="#install" className="transition hover:text-[#111111]">Install</a>
              <a href="#details" className="transition hover:text-[#111111]">Details</a>
              <a href="#faq" className="transition hover:text-[#111111]">FAQ</a>
              <Link to="/download" className="transition hover:text-[#111111]">Download Guide</Link>
              <Link to="/privacy" className="transition hover:text-[#111111]">Privacy</Link>
            </nav>

            <a
              href={apkDownloadHref}
              download
              className="inline-flex items-center gap-2 rounded-full bg-[#111111] px-3 py-2 text-xs font-semibold text-white shadow-[0_18px_40px_rgba(15,23,42,0.14)] transition hover:translate-y-[-1px] sm:px-4 sm:text-sm"
            >
              <span className="sm:hidden">APK</span>
              <span className="hidden sm:inline">Download APK</span>
              <Download size={16} />
            </a>
          </div>
        </header>

        <main id="top">
          <section
            ref={heroRef}
            className="mx-auto grid max-w-7xl gap-7 px-4 pb-12 pt-5 sm:gap-9 sm:px-5 sm:pb-16 sm:pt-8 md:px-8 md:pb-22 md:pt-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center"
          >
            <motion.div
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, ease: 'easeOut' }}
              className="max-w-2xl"
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-black/6 bg-white/84 px-4 py-2 text-sm text-[#52525b] shadow-sm">
                <Sparkles size={15} />
                Official APK download and Android AR tracing app
              </div>

              <h1 className="mt-6 font-display text-[2.2rem] font-semibold leading-[0.95] tracking-[-0.06em] text-[#111111] sm:mt-7 sm:text-[4rem] sm:tracking-[-0.075em] lg:text-[6.05rem]">
                AR Drawing Master
                <span className="block">APK download for</span>
                <span className="block bg-[linear-gradient(135deg,#111827_0%,#2563eb_58%,#7c3aed_100%)] bg-clip-text text-transparent">
                  faster drawing.
                </span>
              </h1>

              <p className="mt-5 max-w-xl text-[0.95rem] leading-6 text-[#52525b] sm:mt-6 sm:text-[1.01rem] sm:leading-7 md:mt-7 md:text-[1.17rem] md:leading-8">
                AR Drawing Master is an official APK download and Android AR tracing app for people who want to draw on real
                paper with guided overlays, templates, AI sketch ideas, and structured beginner practice.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:mt-10 sm:flex-row sm:gap-4">
                <a
                  href={apkDownloadHref}
                  download
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#111111] px-7 py-3.5 text-base font-semibold text-white shadow-[0_24px_55px_rgba(15,23,42,0.15)] transition hover:translate-y-[-1px] sm:w-auto sm:py-4"
                >
                  Download for Android
                  <Download size={18} />
                </a>
                <a
                  href="#workflow"
                  className="inline-flex w-full items-center justify-center rounded-full border border-black/8 bg-white/86 px-7 py-3.5 text-base font-semibold text-[#111111] shadow-[0_12px_30px_rgba(15,23,42,0.05)] transition hover:bg-white sm:w-auto sm:py-4"
                >
                  See how it works
                </a>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-[#71717a] sm:text-sm">
                <span className="rounded-full border border-black/6 bg-white/70 px-3 py-1.5">{apkVersionLabel}</span>
                <span>{apkSizeLabel}</span>
                <span>Direct download on this domain</span>
                <span>AR tracing app for Android</span>
                <span>Official APK download page</span>
                <span>Web version is a showcase only</span>
              </div>

              <div className="mt-6 grid gap-3 sm:mt-7 sm:grid-cols-3">
                {stats.map((item) => (
                  <motion.div
                    key={item.label}
                    whileHover={isMobileLayout ? undefined : { y: -3 }}
                    className="rounded-2xl border border-black/6 bg-white/88 p-4 shadow-[0_10px_25px_rgba(15,23,42,0.05)]"
                  >
                    <div className="font-display text-xl font-semibold tracking-[-0.05em] text-[#111111] sm:text-2xl">{item.value}</div>
                    <div className="mt-1 text-[11px] uppercase tracking-[0.16em] text-[#71717a] sm:text-xs sm:tracking-[0.18em]">{item.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.98, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.85, delay: 0.06, ease: 'easeOut' }}
              style={{ y: sceneY }}
              onPointerMove={handleScenePointerMove}
              onPointerLeave={handleScenePointerLeave}
              className="relative mx-auto w-full max-w-[30rem] [perspective:1800px] sm:max-w-[38rem] md:max-w-[50rem] lg:max-w-[60rem]"
            >
              <motion.div
                style={{ rotateX: allowTilt ? rotateX : 0, rotateY: allowTilt ? rotateY : 0, transformStyle: 'preserve-3d' }}
                className="relative min-h-[21rem] overflow-hidden rounded-[2.1rem] border border-black/6 bg-[linear-gradient(165deg,#ffffff_0%,#edf3ff_45%,#f6f8ff_100%)] p-3.5 shadow-[0_28px_72px_rgba(15,23,42,0.14)] sm:min-h-[31rem] sm:rounded-[2.3rem] sm:p-6 md:rounded-[2.8rem] md:p-8 lg:min-h-[41rem]"
              >
                <div className="pointer-events-none absolute inset-0">
                  <div className="absolute -right-20 top-[-8rem] h-72 w-72 rounded-full bg-[radial-gradient(circle,_rgba(96,165,250,0.32),_rgba(96,165,250,0)_74%)] blur-3xl" />
                  <div className="absolute -left-24 bottom-[-7rem] h-72 w-72 rounded-full bg-[radial-gradient(circle,_rgba(168,85,247,0.24),_rgba(168,85,247,0)_72%)] blur-3xl" />
                </div>

                <motion.div
                  animate={reduceSceneMotion ? undefined : { y: [0, -8, 0] }}
                  transition={reduceSceneMotion ? undefined : { duration: 8.6, repeat: Infinity, ease: 'easeInOut' }}
                  className="absolute left-[8%] top-[14%] hidden w-[30%] rounded-[2rem] border border-white/65 bg-white/75 p-2.5 shadow-[0_22px_60px_rgba(15,23,42,0.15)] backdrop-blur-xl [transform:translateZ(90px)_rotateY(16deg)_rotateX(6deg)] lg:block"
                >
                  <div className="overflow-hidden rounded-[1.4rem] bg-[#0f172a] p-2.5">
                    <div className="mb-2.5 mx-auto h-4 w-20 rounded-full bg-black/85" />
                    <img
                      src={screenTemplates}
                      alt="Template categories in AR Drawing Master"
                      className="block aspect-[9/19] w-full rounded-[1rem] object-cover"
                      style={{ objectPosition: 'center 42%' }}
                      width={720}
                      height={1561}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </motion.div>

                <motion.div
                  animate={reduceSceneMotion ? undefined : { y: [0, 7, 0] }}
                  transition={reduceSceneMotion ? undefined : { duration: 9.2, repeat: Infinity, ease: 'easeInOut', delay: 0.6 }}
                  className="absolute bottom-[8%] right-[7%] hidden w-[29%] rounded-[2rem] border border-white/65 bg-white/75 p-2.5 shadow-[0_22px_60px_rgba(15,23,42,0.15)] backdrop-blur-xl [transform:translateZ(92px)_rotateY(-16deg)_rotateX(7deg)] lg:block"
                >
                  <div className="overflow-hidden rounded-[1.4rem] bg-[#0f172a] p-2.5">
                    <div className="mb-2.5 mx-auto h-4 w-20 rounded-full bg-black/85" />
                    <img
                      src="/onboarding_camera.webp"
                      alt="AR camera tracing mode in AR Drawing Master"
                      className="block aspect-[9/19] w-full rounded-[1rem] object-cover"
                      style={{ objectPosition: 'center 44%' }}
                      width={800}
                      height={800}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </motion.div>

                <motion.div
                  style={{ backgroundImage: glareBackground }}
                  className="pointer-events-none absolute inset-0 rounded-[2.5rem]"
                />

                <motion.div
                  animate={reduceSceneMotion ? undefined : { y: [0, -7, 0], rotateZ: [-0.2, 0.25, -0.2] }}
                  transition={reduceSceneMotion ? undefined : { duration: 10.2, repeat: Infinity, ease: 'easeInOut' }}
                  className="absolute left-1/2 top-[6%] z-30 w-[90%] -translate-x-1/2 sm:w-[78%] md:w-[66%] lg:w-[54%] lg:[transform:translateZ(118px)_rotateY(-3deg)_rotateX(5deg)]"
                >
                  <div className="rounded-[2.6rem] border border-white/65 bg-white/82 p-[10px] shadow-[0_34px_96px_rgba(15,23,42,0.2)] backdrop-blur-2xl ring-1 ring-black/5">
                    <div className="rounded-[2.2rem] bg-[linear-gradient(180deg,#070b14,#0f172a)] p-3">
                      <div className="mx-auto mb-3 h-6 w-24 rounded-full bg-black/90" />
                      <div className="overflow-hidden rounded-[1.9rem] bg-[#eef2ff]">
                        <img
                          src={screenDashboard}
                          alt="AR Drawing Master app dashboard"
                          className="block aspect-[9/19] w-full object-cover"
                          style={{ objectPosition: 'center 34%' }}
                          width={720}
                          height={1561}
                          loading="eager"
                          decoding="async"
                          fetchPriority="high"
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  animate={reduceSceneMotion ? undefined : { y: [0, -10, 0] }}
                  transition={reduceSceneMotion ? undefined : { duration: 10.5, repeat: Infinity, ease: 'easeInOut', delay: 0.2 }}
                  className="absolute bottom-[2%] left-1/2 z-20 hidden w-[95%] -translate-x-1/2 overflow-hidden rounded-[2.4rem] border border-white/58 bg-white/52 p-2.5 shadow-[0_20px_55px_rgba(15,23,42,0.12)] backdrop-blur-2xl sm:block sm:w-[88%] md:bottom-[2%] lg:w-[80%] lg:[transform:translateZ(70px)]"
                >
                  <div className="flex items-center gap-4 rounded-[2rem] bg-[linear-gradient(120deg,rgba(255,255,255,0.94)_0%,rgba(247,250,255,0.82)_100%)] p-3 sm:p-4">
                    <div className="overflow-hidden rounded-[1.2rem] border border-black/6 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.10)]">
                      <img
                        src="/splash-art-card.jpg?v=20260409a"
                        alt="AR trace hero art"
                        className="block h-24 w-24 object-cover sm:h-30 sm:w-30"
                        width={256}
                        height={256}
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-[#64748b]">Premium Visual Engine</p>
                      <p className="mt-1 font-display text-xl font-semibold tracking-[-0.03em] text-[#111111] sm:text-2xl">3D depth with real app flow</p>
                      <p className="mt-1.5 text-sm text-[#475569] sm:text-base">Minimal interface, cinematic motion, and cleaner onboarding clarity.</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  animate={reduceSceneMotion ? undefined : { y: [0, -6, 0] }}
                  transition={reduceSceneMotion ? undefined : { duration: 7.2, repeat: Infinity, ease: 'easeInOut', delay: 0.4 }}
                  className="absolute left-1/2 top-[6%] z-50 hidden -translate-x-1/2 rounded-full border border-white/80 bg-white/92 px-5 py-2.5 text-sm font-medium text-[#111111] shadow-[0_18px_42px_rgba(15,23,42,0.10)] backdrop-blur-xl [transform:translateZ(130px)] md:block lg:left-[30%] lg:translate-x-0"
                >
                  Real app visuals with refined 3D depth
                </motion.div>

                <div className="absolute bottom-3 left-1/2 z-40 -translate-x-1/2 rounded-full border border-white/70 bg-white/90 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#334155] shadow-[0_10px_28px_rgba(15,23,42,0.12)] sm:hidden">
                  Real app preview
                </div>
              </motion.div>
            </motion.div>
          </section>

          <section id="experience" className="border-y border-black/5 bg-white/60">
            <div className="mx-auto max-w-7xl px-4 py-14 sm:px-5 sm:py-18 md:px-8 md:py-24">
              <motion.div {...reveal} className="max-w-3xl">
                <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#94a3b8]">Feature Highlights</div>
                <h2 className="mt-4 font-display text-[2rem] font-semibold tracking-[-0.05em] text-[#111111] sm:text-4xl sm:tracking-[-0.06em] md:text-[3.4rem]">
                  Professional app feel, not a cluttered utility.
                </h2>
                <p className="mt-4 max-w-2xl text-[0.98rem] leading-7 text-[#52525b] sm:mt-5 sm:text-lg sm:leading-8">
                  Every core mode is designed to keep attention on drawing quality: fewer taps, smoother transitions, better practice rhythm.
                </p>
              </motion.div>

              <div className="mt-10 grid gap-5 sm:mt-12 sm:gap-6 lg:grid-cols-3">
                {featureCards.map((card, index) => {
                  const Icon = card.icon;
                  return (
                    <motion.article
                      key={card.title}
                      initial={{ opacity: 0, y: 26 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.22 }}
                      transition={{ duration: 0.55, delay: index * 0.08, ease: 'easeOut' }}
                      whileHover={isMobileLayout ? undefined : { y: -6, scale: 1.01 }}
                      className="group overflow-hidden rounded-[2rem] border border-black/6 bg-white/92 shadow-[0_18px_48px_rgba(15,23,42,0.08)]"
                    >
                      <div className={`relative h-48 bg-gradient-to-br sm:h-52 ${card.tone}`}>
                        <img
                          src={card.image}
                          alt={card.title}
                          className="h-full w-full object-cover object-top transition duration-500 group-hover:scale-[1.03]"
                          width={720}
                          height={1561}
                          loading="lazy"
                          decoding="async"
                        />
                        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(17,24,39,0)_20%,rgba(17,24,39,0.25)_100%)]" />
                        <div className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full border border-white/50 bg-white/80 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[#334155] backdrop-blur">
                          <Icon size={14} />
                          Mode
                        </div>
                      </div>
                      <div className="p-5 sm:p-6">
                        <h3 className="font-display text-[1.5rem] font-semibold tracking-[-0.03em] text-[#111111] sm:text-2xl sm:tracking-[-0.04em]">{card.title}</h3>
                        <p className="mt-3 text-[0.95rem] leading-6 text-[#52525b] sm:text-base sm:leading-7">{card.body}</p>
                      </div>
                    </motion.article>
                  );
                })}
              </div>
            </div>
          </section>

          <section id="workflow" className="mx-auto max-w-7xl px-4 py-14 sm:px-5 sm:py-18 md:px-8 md:py-24">
            <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
              <motion.div
                {...reveal}
                className="rounded-[2.2rem] border border-black/6 bg-[linear-gradient(180deg,#ffffff,#fafaf8)] p-6 shadow-[0_22px_60px_rgba(15,23,42,0.06)] sm:p-8"
              >
                <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#94a3b8]">Workflow</div>
                <h2 className="mt-4 font-display text-[2rem] font-semibold tracking-[-0.05em] text-[#111111] sm:text-4xl sm:tracking-[-0.06em] md:text-[3.1rem]">
                  Three steps. Zero confusion.
                </h2>

                <div className="mt-8 space-y-4">
                  {workflowSteps.map((step, index) => (
                    <motion.div
                      key={step.title}
                      whileHover={isMobileLayout ? undefined : { x: 3 }}
                      className="flex gap-4 rounded-[1.7rem] border border-black/5 bg-white/92 px-5 py-5 shadow-[0_10px_28px_rgba(15,23,42,0.04)]"
                    >
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#111111] text-sm font-semibold text-white">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-semibold text-[#111111]">{step.title}</p>
                        <p className="mt-1 text-[0.95rem] leading-6 text-[#3f3f46] sm:text-base sm:leading-7">{step.body}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                {...reveal}
                id="details"
                className="rounded-[2.2rem] border border-black/6 bg-[#101828] p-6 text-white shadow-[0_30px_80px_rgba(15,23,42,0.16)] sm:p-8"
              >
                <div className="text-xs font-semibold uppercase tracking-[0.3em] text-white/45">Inside The APK</div>
                <h2 className="mt-4 font-display text-[2rem] font-semibold tracking-[-0.05em] sm:text-4xl sm:tracking-[-0.06em] md:text-[3.1rem]">
                  Built for long-term drawing progress.
                </h2>
                <p className="mt-5 text-[0.98rem] leading-7 text-white/72 sm:text-lg sm:leading-8">
                  The goal is consistent practice quality: clean tracing sessions, better reference flow, and repeatable improvement loops.
                </p>

                <div className="mt-8 grid gap-4">
                  {detailItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <div key={item.title} className="rounded-[1.5rem] border border-white/10 bg-white/5 px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="rounded-xl bg-white/10 p-2 text-[#93c5fd]">
                            <Icon size={18} />
                          </div>
                          <p className="font-semibold text-white">{item.title}</p>
                        </div>
                        <p className="mt-2 text-sm leading-7 text-white/72">{item.body}</p>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-8 flex flex-wrap gap-3 text-sm">
                  <span className="rounded-full border border-white/20 bg-white/8 px-3 py-1.5">{apkVersionLabel}</span>
                  <span className="rounded-full border border-white/20 bg-white/8 px-3 py-1.5">{apkSizeLabel}</span>
                  <span className="rounded-full border border-white/20 bg-white/8 px-3 py-1.5">Showcase website only</span>
                </div>
              </motion.div>
            </div>
          </section>

          <section id="install" className="mx-auto max-w-7xl px-4 py-4 pb-20 sm:px-5 md:px-8 md:pb-24">
            <motion.div
              {...reveal}
              className="rounded-[2.2rem] border border-black/6 bg-white/90 p-6 shadow-[0_20px_56px_rgba(15,23,42,0.07)] sm:p-8"
            >
              <div className="grid gap-8 lg:grid-cols-[0.94fr_1.06fr] lg:items-start">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#94a3b8]">Install Guide</div>
                  <h2 className="mt-4 font-display text-[2rem] font-semibold tracking-[-0.05em] text-[#111111] sm:text-4xl sm:tracking-[-0.06em] md:text-[3rem]">
                    Download once, install in minutes.
                  </h2>
                  <p className="mt-5 max-w-xl text-[0.98rem] leading-7 text-[#52525b] md:text-lg">
                    Quick setup for new users. The website stays lightweight and the full drawing system runs in the Android app.
                  </p>

                  <div className="mt-7 rounded-2xl border border-black/6 bg-[#f8fbff] p-5">
                    <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#64748b]">Compatibility</p>
                    <ul className="mt-3 space-y-2 text-sm leading-7 text-[#475569]">
                      <li>Android phones and tablets</li>
                      <li>Recommended: modern device with stable camera</li>
                      <li>Web browser is showcase + download only</li>
                    </ul>
                  </div>

                  <div className="mt-4 rounded-2xl border border-black/6 bg-white p-5 shadow-[0_10px_24px_rgba(15,23,42,0.05)]">
                    <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#64748b]">Release Highlights</p>
                    <p className="mt-1 text-sm text-[#475569]">{apkVersionLabel} | {apkSizeLabel} | {apkUpdatedLabel}</p>
                    <ul className="mt-3 space-y-2 text-sm leading-7 text-[#334155]">
                      {releaseHighlights.map((item) => (
                        <li key={item} className="flex gap-2">
                          <span aria-hidden="true">-</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="space-y-3">
                  {installSteps.map((step, index) => (
                    <motion.div
                      key={step}
                      whileHover={isMobileLayout ? undefined : { x: 3 }}
                      className="flex gap-4 rounded-2xl border border-black/6 bg-white p-5 shadow-[0_10px_24px_rgba(15,23,42,0.05)]"
                    >
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#111111] text-sm font-semibold text-white">
                        {index + 1}
                      </div>
                      <p className="pt-1 text-sm leading-7 text-[#334155] md:text-base">{step}</p>
                    </motion.div>
                  ))}

                  <a
                    href={apkDownloadHref}
                    download
                    className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#111111] px-7 py-4 text-base font-semibold text-white shadow-[0_24px_55px_rgba(15,23,42,0.15)] transition hover:translate-y-[-1px]"
                  >
                    Download APK Now
                    <Download size={18} />
                  </a>
                </div>
              </div>
            </motion.div>
          </section>

          <section id="faq" className="mx-auto max-w-7xl px-4 pb-28 sm:px-5 md:px-8 md:pb-30">
            <motion.div
              {...reveal}
              className="rounded-[2.5rem] border border-black/6 bg-[linear-gradient(135deg,#ffffff_0%,#f8fbff_48%,#f7f1ff_100%)] p-6 shadow-[0_28px_80px_rgba(15,23,42,0.08)] sm:p-8 md:p-12"
            >
              <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#94a3b8]">FAQ & Download</div>
                  <h2 className="mt-4 font-display text-[2rem] font-semibold tracking-[-0.05em] text-[#111111] sm:text-4xl sm:tracking-[-0.06em] md:text-[3.4rem]">
                    Everything you need before installing.
                  </h2>
                  <p className="mt-5 max-w-xl text-[0.98rem] leading-7 text-[#52525b] sm:text-lg sm:leading-8">
                    This website explains the product and gives you the direct installer. Drawing features run inside the Android app.
                  </p>

                  <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                    <a
                      href={apkDownloadHref}
                      download
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-[#111111] px-7 py-4 text-base font-semibold text-white shadow-[0_24px_55px_rgba(15,23,42,0.15)] transition hover:translate-y-[-1px]"
                    >
                      Download APK
                      <Download size={18} />
                    </a>
                    <Link
                      to="/privacy"
                      className="inline-flex items-center justify-center rounded-full border border-black/8 bg-white/86 px-7 py-4 text-base font-semibold text-[#111111] transition hover:bg-white"
                    >
                      Privacy Policy
                    </Link>
                  </div>
                </div>

                <div className="space-y-3">
                  {faqItems.map((item) => (
                    <details
                      key={item.q}
                      className="rounded-2xl border border-black/6 bg-white/88 p-5 shadow-[0_10px_26px_rgba(15,23,42,0.05)]"
                    >
                      <summary className="cursor-pointer list-none font-semibold text-[#111111]">{item.q}</summary>
                      <p className="mt-3 text-sm leading-7 text-[#52525b]">{item.a}</p>
                    </details>
                  ))}

                  <div className="rounded-2xl border border-black/6 bg-white/88 p-5 shadow-[0_10px_26px_rgba(15,23,42,0.05)]">
                    <p className="font-semibold text-[#111111]">Explore more product pages</p>
                    <div className="mt-3 flex flex-wrap gap-3 text-sm text-[#334155]">
                      <Link to="/features/ar-tracing" className="rounded-full border border-black/6 px-3 py-2 transition hover:bg-[#fafaf8]">AR Tracing</Link>
                      <Link to="/ar-tracing-app" className="rounded-full border border-black/6 px-3 py-2 transition hover:bg-[#fafaf8]">AR Tracing App</Link>
                      <Link to="/features/ai-sketch-generator" className="rounded-full border border-black/6 px-3 py-2 transition hover:bg-[#fafaf8]">AI Sketch</Link>
                      <Link to="/features/template-library" className="rounded-full border border-black/6 px-3 py-2 transition hover:bg-[#fafaf8]">Templates</Link>
                      <Link to="/drawing-app-apk" className="rounded-full border border-black/6 px-3 py-2 transition hover:bg-[#fafaf8]">Drawing App APK</Link>
                      <Link to="/apk-download-for-ar-drawing" className="rounded-full border border-black/6 px-3 py-2 transition hover:bg-[#fafaf8]">AR Drawing APK</Link>
                      <Link to="/how-to-use" className="rounded-full border border-black/6 px-3 py-2 transition hover:bg-[#fafaf8]">How To Use</Link>
                      <Link to="/faq" className="rounded-full border border-black/6 px-3 py-2 transition hover:bg-[#fafaf8]">FAQ</Link>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </section>
        </main>

        <div className="fixed inset-x-0 bottom-0 z-50 border-t border-black/10 bg-white/92 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] backdrop-blur-xl md:hidden">
          <a
            href={apkDownloadHref}
            download
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#111111] px-5 py-3.5 text-sm font-semibold text-white shadow-[0_18px_40px_rgba(15,23,42,0.2)]"
          >
            Download APK ({apkVersionLabel} | {apkSizeLabel})
            <Download size={16} />
          </a>
        </div>

        <footer className="border-t border-black/5 bg-white/70">
          <div className="mx-auto flex max-w-7xl flex-col gap-4 px-5 py-7 text-sm text-[#71717a] md:flex-row md:items-center md:justify-between md:px-8">
            <div className="flex items-center gap-3">
              <img
                src="/logo-96.png?v=20260409a"
                alt="AR Drawing Master logo"
                className="h-9 w-9 rounded-xl object-cover"
                width={96}
                height={96}
                loading="lazy"
                decoding="async"
              />
              <span>Copyright 2026 AR Drawing Master. All rights reserved.</span>
            </div>

            <div className="flex flex-wrap items-center gap-5">
              <a href="#experience" className="transition hover:text-[#111111]">Experience</a>
              <a href="#workflow" className="transition hover:text-[#111111]">Workflow</a>
              <a href="#install" className="transition hover:text-[#111111]">Install</a>
              <a href="#details" className="transition hover:text-[#111111]">Details</a>
              <a href="#faq" className="transition hover:text-[#111111]">FAQ</a>
              <Link to="/download" className="transition hover:text-[#111111]">Download Guide</Link>
              <Link to="/features/ar-tracing" className="transition hover:text-[#111111]">AR Tracing</Link>
              <Link to="/ar-tracing-app" className="transition hover:text-[#111111]">AR Tracing App</Link>
              <Link to="/features/template-library" className="transition hover:text-[#111111]">Templates</Link>
              <Link to="/drawing-app-apk" className="transition hover:text-[#111111]">Drawing App APK</Link>
              <Link to="/apk-download-for-ar-drawing" className="transition hover:text-[#111111]">AR Drawing APK</Link>
              <Link to="/how-to-use" className="transition hover:text-[#111111]">How To Use</Link>
              <Link to="/privacy" className="transition hover:text-[#111111]">Privacy</Link>
              <a href={apkDownloadHref} download className="transition hover:text-[#111111]">Download APK</a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};


